// Grid trade netting + break-even helpers — ported from legacy ui/index.html.
// Kept in a dedicated module so both the trades table and detail page share
// identical logic without duplicating any computation.

import type { Trade, Order } from "@/types/api";

const MAKER_FEE_RATE = 0.0002;
const TAKER_FEE_RATE = 0.0007;

/**
 * Build a Map<tradeId, counterpartTrade> for offsetting unmatched openings.
 * Greedy oldest-first pairing: opposite side, same qty (within 1e-9).
 */
export function buildNettedByMap(trades: Trade[]): Map<number, Trade> {
  const nettedBy = new Map<number, Trade>();
  const unmatched = trades
    .filter((t) => t.matched === 0)
    .sort((a, b) => new Date(a.traded_at).getTime() - new Date(b.traded_at).getTime());

  for (const a of unmatched) {
    if (nettedBy.has(a.id)) continue;
    const counter = unmatched.find(
      (b) =>
        b.id !== a.id &&
        !nettedBy.has(b.id) &&
        b.side !== a.side &&
        Math.abs(b.quantity - a.quantity) < 1e-9
    );
    if (counter) {
      nettedBy.set(a.id, counter);
      nettedBy.set(counter.id, a);
    }
  }
  return nettedBy;
}

/**
 * Sort trades: unmatched openings first, then newest-first.
 */
export function sortTrades(trades: Trade[]): Trade[] {
  return [...trades].sort((a, b) => {
    const aOpen = a.matched === 0 ? 1 : 0;
    const bOpen = b.matched === 0 ? 1 : 0;
    if (aOpen !== bOpen) return bOpen - aOpen;
    return new Date(b.traded_at).getTime() - new Date(a.traded_at).getTime();
  });
}

/**
 * Break-even price for an opening trade leg.
 * After 2× max_hold_minutes the BE limit is post-only (maker rate).
 */
export function tradeBreakEven(t: Trade, rate?: number): number {
  if (!t.quantity) return 0;
  const feePerUnit = (t.fee ?? 0) / t.quantity;
  const r = rate ?? TAKER_FEE_RATE;
  return t.side === "buy"
    ? (t.price + feePerUnit) / (1 - r)
    : (t.price - feePerUnit) / (1 + r);
}

/** Whether the BE target has relaxed to maker rate (age ≥ 2× max_hold). */
export function tradeIsRelaxedBE(t: Trade, maxHoldMinutes: number | null): boolean {
  if (!maxHoldMinutes) return false;
  const maxHoldMs = maxHoldMinutes * 60_000;
  const age = Date.now() - new Date(t.traded_at).getTime();
  return age >= maxHoldMs * 2;
}

/** Find the on-book BE order for an opening trade, if any. */
export function findBeOrder(t: Trade, orders: Order[]): Order | undefined {
  return orders.find(
    (o) =>
      o.opening_trade_id === t.id &&
      o.close_reason === "break-even" &&
      (o.status === "open" || o.status === "pending")
  );
}

export { MAKER_FEE_RATE, TAKER_FEE_RATE };
